import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import { pageViewsAPI } from './utils/api';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import HomePage from './pages/HomePage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import ConsultationPage from './pages/ConsultationPage';
import QuizPage from './pages/QuizPage';
import QuizListPage from './pages/QuizListPage';
import LoginPage from './pages/LoginPage';
import AboutPage from './pages/AboutPage';
import NotFoundPage from './pages/NotFoundPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import UnsubscribePage from './pages/UnsubscribePage';
import ProfilePage from './pages/ProfilePage';

const App: React.FC = () => {
  const location = useLocation();

  // Track page views
  useEffect(() => {
    // Record page view when location changes
    const recordPageView = async () => {
      try {
        // Don't track admin pages
        if (!location.pathname.startsWith('/admin')) {
          // Check if this page has already been viewed in this session
          const viewedPages = JSON.parse(sessionStorage.getItem('viewedPages') || '{}');
          
          // Only record a page view if this page hasn't been viewed in this session
          if (!viewedPages[location.pathname]) {
            await pageViewsAPI.recordPageView(location.pathname);
            
            // Mark this page as viewed in this session
            viewedPages[location.pathname] = true;
            sessionStorage.setItem('viewedPages', JSON.stringify(viewedPages));
          }
        }
      } catch (error) {
        // Silently fail - we don't want to disrupt the user experience
      }
    };

    recordPageView();
  }, [location.pathname]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/consultation" element={<ConsultationPage />} />
          <Route path="/quizzes" element={<QuizListPage />} />
          <Route path="/quiz/:id" element={<QuizPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          
          {/* Protected Routes (require login) */}
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } 
          />
          
          {/* Admin Routes */}
          <Route 
            path="/admin/*" 
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardPage />
              </ProtectedRoute>
            } 
          />
          
          {/* 404 Route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

export default App;
